import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../../components/ui/command';
import {
  LayoutDashboard,
  Building2,
  Users,
  User,
  Bot,
  Wallet,
  ShieldCheck,
  FileText,
  Activity,
  Eye,
  ClipboardCheck,
  PowerOff,
  Command as CommandIcon,
} from 'lucide-react';
import { governanceAPI } from '../../../lib/governanceApi';

/**
 * <CommandPalette />
 *
 * Global ⌘K launcher for the governance panel:
 *   1. Search results — debounced cross-tenant search (/api/ai/ops/sprint1/search)
 *      grouped into Users / Tenants / Organizations, each jumping to its detail page.
 *   2. Quick actions  — jump to high-intent ops destinations (approvals, paused agents,
 *      pause-a-tenant kill switch).
 *   3. Navigation     — every sidebar destination.
 *
 * Mounts once at the AdminLayout root. Listens for ⌘K / Ctrl+K.
 */
const NAV_ITEMS = [
  { label: 'Dashboard',       path: '/governance/dashboard',            icon: LayoutDashboard },
  { label: 'Approvals',       path: '/governance/approvals',            icon: ClipboardCheck },
  { label: 'Users',           path: '/governance/users',                icon: Users },
  { label: 'Organizations',   path: '/governance/organizations',        icon: Building2 },
  { label: 'Tenants',         path: '/governance/tenants',              icon: Users },
  { label: 'Agents',          path: '/governance/agents',               icon: Bot },
  { label: 'Economics',       path: '/governance/economics',            icon: Wallet },
  { label: 'Capability',      path: '/governance/capability',           icon: ShieldCheck },
  { label: 'Budgets',         path: '/governance/budgets',              icon: Wallet },
  { label: 'Confidence',      path: '/governance/confidence',           icon: ShieldCheck },
  { label: 'Audit Logs',      path: '/governance/audit',                icon: FileText },
  { label: 'Service Subs',    path: '/governance/service-subscriptions', icon: Activity },
  { label: 'Investor View',   path: '/governance/investor',             icon: Eye },
  { label: 'Founder Ops',     path: '/governance/founder-ops',          icon: Activity },
];

const QUICK_ACTIONS = [
  { label: 'Review pending approvals', path: '/governance/approvals', icon: ClipboardCheck, testid: 'approvals' },
  { label: 'Resume auto-paused agents', path: '/governance/agents', icon: Bot, testid: 'agents' },
  { label: 'Pause a tenant\u2019s AI (kill switch)', path: '/governance/tenants', icon: PowerOff, testid: 'kill-switch', danger: true },
];

export default function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState(null); // { users:[], tenants:[], organizations:[] }
  const [searching, setSearching] = useState(false);

  // Global ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Debounced cross-tenant search
  useEffect(() => {
    if (!query || query.length < 2) {
      setGroups(null);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await governanceAPI.crossTenantSearch(query, 8);
        // Endpoint returns { results: { users:[], tenants:[], organizations:[], leads:[] } }
        setGroups(r.data?.results || null);
      } catch (_e) {
        setGroups(null);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const goTo = useCallback((path) => {
    setOpen(false);
    setQuery('');
    setGroups(null);
    navigate(path);
  }, [navigate]);

  const hasHits = !!groups && (
    (groups.users?.length || 0) + (groups.tenants?.length || 0) + (groups.organizations?.length || 0) > 0
  );

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      {/* Disable cmdk's built-in fuzzy filter while showing server-side search hits */}
      <Command shouldFilter={!hasHits}>
        <CommandInput
          placeholder="Search tenants, users, orgs, or jump to a page…"
          value={query}
          onValueChange={setQuery}
          data-testid="gov-cmdk-input"
        />
        <CommandList data-testid="gov-cmdk-list">
          <CommandEmpty>{searching ? 'Searching…' : 'No results.'}</CommandEmpty>

          {/* Search hits — grouped */}
          {hasHits && (
            <>
              {groups.users?.length > 0 && (
                <CommandGroup heading="Users">
                  {groups.users.map((u, i) => (
                    <CommandItem
                      key={`user-${u.id}`}
                      value={`user ${u.name} ${u.email} ${u.id}`}
                      onSelect={() => goTo(`/governance/users/${u.id}`)}
                      data-testid={`gov-cmdk-user-${i}`}
                    >
                      <User className="mr-2 h-3.5 w-3.5" />
                      <span className="flex-1 truncate">{u.name || u.email}</span>
                      <span className="gov-text-tertiary ml-2 truncate text-[11px]">{u.email}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {groups.tenants?.length > 0 && (
                <CommandGroup heading="Tenants">
                  {groups.tenants.map((t, i) => (
                    <CommandItem
                      key={`tenant-${t.id}`}
                      value={`tenant ${t.name} ${t.id} ${t.industry || ''}`}
                      onSelect={() => goTo(`/governance/tenants/${t.id}`)}
                      data-testid={`gov-cmdk-tenant-${i}`}
                    >
                      <Building2 className="mr-2 h-3.5 w-3.5" />
                      <span className="flex-1 truncate">{t.name || t.id}</span>
                      {t.tenant_type && <span className="gov-text-tertiary ml-2 text-[10px] uppercase">{t.tenant_type}</span>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {groups.organizations?.length > 0 && (
                <CommandGroup heading="Organizations">
                  {groups.organizations.map((o, i) => (
                    <CommandItem
                      key={`org-${o.id}`}
                      value={`org ${o.name} ${o.id}`}
                      onSelect={() => goTo(`/governance/organizations/${o.id}`)}
                      data-testid={`gov-cmdk-org-${i}`}
                    >
                      <Users className="mr-2 h-3.5 w-3.5" />
                      <span className="flex-1 truncate">{o.name || o.id}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              <CommandSeparator />
            </>
          )}

          {/* Quick actions */}
          <CommandGroup heading="Quick Actions">
            {QUICK_ACTIONS.map((qa) => {
              const Icon = qa.icon;
              return (
                <CommandItem
                  key={qa.path + qa.label}
                  onSelect={() => goTo(qa.path)}
                  data-testid={`gov-cmdk-action-${qa.testid}`}
                >
                  <Icon className={`mr-2 h-3.5 w-3.5 ${qa.danger ? 'text-rose-400' : ''}`} />
                  <span className={qa.danger ? 'text-rose-400' : ''}>{qa.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>

          <CommandSeparator />

          {/* Navigation */}
          <CommandGroup heading="Navigate">
            {NAV_ITEMS.map((nav) => {
              const Icon = nav.icon;
              return (
                <CommandItem
                  key={nav.path}
                  onSelect={() => goTo(nav.path)}
                  data-testid={`gov-cmdk-nav-${nav.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="mr-2 h-3.5 w-3.5" />
                  <span>{nav.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </Command>
    </CommandDialog>
  );
}

// Tiny hint chip rendered in the page header (visual cue for ⌘K)
export function CommandHint() {
  return (
    <span className="gov-text-tertiary hidden items-center gap-1 rounded border border-[var(--gov-border)] bg-[var(--gov-bg-input)] px-1.5 py-0.5 text-[11px] sm:inline-flex">
      <CommandIcon className="h-3 w-3" /> K
    </span>
  );
}
